import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useLocation } from 'react-router-dom'

import {
  checkEmail,
  linkEmail,
  LoginState,
  sendCustomerVerifEmail,
  userLogin,
  userRegister,
  userThirdLogin
} from '@api/home/store/slice/loginSlice'
import winPixel from '@api/hernest/winPixel_m.bak'
import Subscription, { resetPopupStatus } from '@ui-pc/Subscription'
import {
  fbSdkCallback,
  getSearchParams,
  ggSdkCallback,
  isBrowser,
  loTrack
} from '@utils'
import {
  createEmailItemData,
  createFullnameItemData,
  createPasswordItemData
} from '@utils/form'
import { pathnameToPage } from '@utils/winPixel'

interface RegisterState {
  lengthPremission: number
  containLetterPremission: number
  containNumberPremission: number
}

const regState = {
  lengthPremission: 0,
  // 有包含字母
  containLetterPremission: 0,
  // 有包含字母
  containNumberPremission: 0
}

// 是否有包含字母
const containLetterReg = /[a-zA-Z]/
// 是否有包含数字
const containNumberReg = /\d/

const fbAppIdBeta = {
  hernest: '683308623231680', // beta-密钥: 4e6886c72fa03192ad2115d481ac07af
  home: '683308623231680' // beta-密钥: 4e6886c72fa03192ad2115d481ac07af
}

const fbAppIdProd = {
  hernest: '683308623231680', // 正式
  home: '683308623231680' // 正式
}

const ggAppIdBeta = {
  hernest:
    '835903017825-jlcnoim3gv4j597qfag8g4nemcs3po3l.apps.googleusercontent.com', // beta-密钥: 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
  home: '835903017825-jlcnoim3gv4j597qfag8g4nemcs3po3l.apps.googleusercontent.com' // beta-密钥: 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
}

const ggAppIdProd = {
  hernest:
    '823842053739-n651vfalrn9lomcqascrsq18396q71de.apps.googleusercontent.com', // 正式-密钥: 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
  home: '330406636882-1k76pf48foi95chqtcn7pssq27f0j6bp.apps.googleusercontent.com' // 正式-密钥: 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
}

export const useLogin = ({
  isHernest = false,
  isHome = false,
  isMobile = false
}) => {
  const dispatch = useDispatch()
  const uselocation = useLocation()
  const navigate = useNavigate()
  let isProd = false
  if (isBrowser()) {
    isProd = /^(www\.|pc\.|m\.|pre\.|m-pre\.)/.test(location.hostname) // pre和生产用正式id
  }
  const siteTmp = isHernest ? 'hernest' : 'home'
  const fbAppId = isProd ? fbAppIdProd[siteTmp] : fbAppIdBeta[siteTmp]
  const ggAppId = isProd ? ggAppIdProd[siteTmp] : ggAppIdBeta[siteTmp]

  const urlParams = getSearchParams(uselocation.search, [
    'backUrl',
    'account'
  ] as const)
  // 注册券包
  const [couponList, setCouponList] = useState([])
  const [couponVisible, setCouponVisible] = useState(false)
  const loginState: LoginState = useSelector(
    (state: any) => state.login,
    shallowEqual
  )
  const subText = useSelector((state: any) => state.app.subscriptionText)

  const handleCancel = () => {
    dispatch({
      type: 'login/triggleLogin',
      payload: false
    })
    toPanelType(1)
  }
  // 面板内容显示: 1-登录 2-注册
  const [panelType, setPanelType] = useState(1)

  // email配置
  const [emailConfig, setEmailConfig] = useState(
    createEmailItemData({
      placeholder: ''
    })
  )

  // 绑定邮箱-email配置-(pc端绑定邮箱：需要跟登录注册的邮箱隔离)
  const [bindEmailConfig, setBindEmailConfig] = useState(
    createEmailItemData({
      placeholder: ''
    })
  )

  // password配置
  const [passwordConfig, setPasswordConfig] = useState(
    createPasswordItemData({
      clearable: false,
      placeholder: '',
      customError: panelType === 2
    })
  )

  // fullname配置
  const [fullnameConfig, setFullnameConfig] = useState(
    createFullnameItemData({
      length: 25
    })
  )

  // 注册订阅
  const [checked, useChecked] = useState(true)
  const [showRegPwdError, setShowRegPwdError] = useState(false)

  // 注册密码状态--0: 初始状态 1: 验证通过 2: 验证取消
  const [registerState, setRegisterState] = useState<RegisterState>({
    ...regState
  })

  const validateRegisterPassword = (val: string): boolean => {
    if (!val) {
      setRegisterState({ ...regState })
      return false
    }

    let flag = true

    if (val.length < 6 || val.length > 20) {
      setRegisterState((prevState) => ({
        ...prevState,
        lengthPremission: 2
      }))
      flag = false
    } else {
      setRegisterState((prevState) => ({
        ...prevState,
        lengthPremission: 1
      }))
    }

    if (containLetterReg.test(val)) {
      setRegisterState((prevState) => ({
        ...prevState,
        containLetterPremission: 1
      }))
    } else {
      setRegisterState((prevState) => ({
        ...prevState,
        containLetterPremission: 2
      }))
      flag = false
    }

    if (containNumberReg.test(val)) {
      setRegisterState((prevState) => ({
        ...prevState,
        containNumberPremission: 1
      }))
    } else {
      setRegisterState((prevState) => ({
        ...prevState,
        containNumberPremission: 2
      }))
      flag = false
    }

    return flag
  }

  // 失焦校验
  const handleBlur = (type: string, isRegister?: boolean) => {
    if (type === 'email') {
      const pass = emailConfig.validate()
      setEmailConfig((prevConfig: any) => ({
        ...prevConfig,
        hasError: !pass
      }))
    } else if (type === 'bindEmail') {
      const pass = bindEmailConfig.validate()
      setBindEmailConfig((prevConfig: any) => ({
        ...prevConfig,
        hasError: !pass
      }))
    } else if (type === 'password') {
      if (isRegister) {
        const pass = validateRegisterPassword(passwordConfig.value)
        setPasswordConfig((prevConfig: any) => ({
          ...prevConfig,
          hasError: !pass
        }))
      } else {
        // 登录无需校验
        setPasswordConfig((prevConfig: any) => ({
          ...prevConfig,
          error: '',
          hasError: !passwordConfig.value
        }))
      }
    } else if (type === 'fullname') {
      const pass = fullnameConfig.validate()
      setFullnameConfig((prevConfig: any) => ({
        ...prevConfig,
        hasError: !pass
      }))
    }
  }

  // 接口提交前--表单校验
  const validateForm = ({
    isRegister = false,
    isLogin = false,
    isBind = false
  }) => {
    let flag = true
    const fn = (prevConfig: any) => ({
      ...prevConfig,
      hasError: true
    })
    // 绑定邮箱
    if (isBind) {
      const passEmail = bindEmailConfig.validate()
      if (!passEmail) {
        setBindEmailConfig(fn)
        flag = false
      }
    } else {
      const passEmail = emailConfig.validate()
      if (!passEmail) {
        setEmailConfig(fn)
        flag = false
      }
    }

    if (isRegister) {
      const passPassword = validateRegisterPassword(passwordConfig.value)
      if (!passPassword) {
        setPasswordConfig(fn)
        setShowRegPwdError(true)
        flag = false
      }

      if (!fullnameConfig.value) {
        setFullnameConfig(fn)
        flag = false
      }
    } else if (isLogin) {
      if (!passwordConfig.value) {
        setPasswordConfig(fn)
        flag = false
      }
    }

    return flag
  }

  // 输入值时操作
  const hanldeUpdateValue = (
    value: string,
    type: string,
    isRegister?: boolean
  ) => {
    const fn = (prevConfig: any) => ({
      ...prevConfig,
      value,
      hasError: false,
      error: ''
    })
    if (type === 'email') {
      setEmailConfig(fn)
    } else if (type === 'bindEmail') {
      const pass = bindEmailConfig.validate()
      setBindEmailConfig(fn)
    } else if (type === 'password') {
      if (isRegister) {
        validateRegisterPassword(value)
        setPasswordConfig(fn)
        setShowRegPwdError(true)
      } else {
        setPasswordConfig(fn)
      }
    } else if (type === 'fullname') {
      setFullnameConfig(fn)
    }
  }

  // 重置所有状态
  const resetFormData = (isRegister?: boolean) => {
    const fn = (prevConfig: any) => ({
      ...prevConfig,
      value: '',
      error: '',
      hasError: false
    })
    setEmailConfig(fn)
    setBindEmailConfig(fn)
    setPasswordConfig((prevConfig: any) => ({
      ...prevConfig,
      value: '',
      error: '',
      hasError: false,
      customError: isRegister
    }))
    setShowRegPwdError(false)
    setFullnameConfig(fn)
    setRegisterState({ ...regState })
    useChecked(true)
    setCouponList([])
    setCouponVisible(false)
    showResetModal(false)
    showBindModal(false)
    setBindLoading(false)
    setFbLoading(false)
    setGgLoading(false)
    setErrorTips('')
  }

  // 切换面板-重置状态
  const toPanelType = (type: number) => {
    setPanelType(type)
    resetFormData(type === 2)
  }

  const [btnLoading, setBtnLoading] = useState(false)
  // 提交接口后的错误提示语
  const [errorTips, setErrorTips] = useState('')

  //登录和注册成功后的回调函数
  const loginCallback = (data?: any, isRegister?: boolean) => {
    // 更新用户信息
    const userData = isRegister ? data.customerInfo : data
    dispatch({
      type: 'app/updateUserInfo',
      payload: userData
    })
    // 重置所有数据
    if (!isMobile) {
      handleCancel()
    }
    message.success(
      `${isRegister ? 'Successfully Registered' : 'Login successful'}`
    )
    loginState.callback?.()
    // m端: 有回退链接时
    isMobile && urlParams.backUrl && navigate(urlParams.backUrl)
  }

  // 提交登录
  const handleSignIn = () => {
    if (isMobile) {
      isHernest ? loTrack('M_Account_Sign in') : loTrack('Account_Sign in')
    } else {
      isHernest
        ? loTrack('PC_Sign in Popup_Sign in')
        : loTrack('Sign in Popup_Sign in')
    }
    const isPassValidate = validateForm({ isLogin: true })
    if (!isPassValidate) {
      return
    } else {
      // 重置
      setEmailConfig({
        ...emailConfig,
        hasError: false,
        error: ''
      })
      setPasswordConfig({
        ...passwordConfig,
        hasError: false,
        error: ''
      })
    }
    setBtnLoading(true)
    winPixel.login(emailConfig.value)
    const params = {
      email: emailConfig.value,
      password: passwordConfig.value
    }
    userLogin(params)
      .then((res) => {
        // 登录成功
        if (res.code === 200) {
          loginCallback(res.data)
        } else if (res.code === 510) {
          // 约定510: 邮箱未注册
          setEmailConfig({
            ...emailConfig,
            hasError: true,
            error: res.message
          })
        } else if (res.code === 610) {
          // 约定610: 密码相关的错误提示
          setPasswordConfig({
            ...passwordConfig,
            hasError: true,
            error: res.message
          })
        }
      })
      .catch((err) => {
        setPasswordConfig({
          ...passwordConfig,
          hasError: true,
          error: err.message
        })
      })
      .finally(() => {
        setBtnLoading(false)
      })
  }

  // 提交注册相关
  const handleRegister = () => {
    const isPassValidate = validateForm({ isRegister: true })
    if (!isPassValidate) {
      return
    }
    setBtnLoading(true)
    winPixel.signUp('Driect', emailConfig.value)
    if (isMobile) {
      isHernest ? loTrack('M_Account_Register') : loTrack('Account_Register')
    } else {
      loTrack('Sign in Popup_Register')
    }
    const params = {
      isSubscribe: +checked,
      email: emailConfig.value,
      password: passwordConfig.value,
      rePassword: passwordConfig.value,
      fullName: fullnameConfig.value
    }

    userRegister(params)
      .then((res) => {
        if (res.code === 200) {
          loginCallback(res.data, true) // 注册成功自动登陆
          setCouponList(res.data.couponList)
          //setCouponVisible(true)
          resetPopupStatus()
          if (checked && res.data?.customerInfo?.couponCode) {
            Subscription.open({ subData: res.data?.customerInfo })
          }
          checked &&
            winPixel.subscribe(
              params.email,
              pathnameToPage(uselocation.pathname),
              'Subscribe',
              'Register'
            )
        } else if (res.code === 511) {
          // 约定511: 邮箱已注册
          setEmailConfig({
            ...emailConfig,
            hasError: true,
            error: (
              <span>
                This email has already been used for an account.
                <br />
                <a onClick={() => toPanelType(1)}>Sign in</a> or{' '}
                <a onClick={() => handleForgotPwd()}>reset your password</a>.
                Sign in with Google or Facebook if you have previously used this
                method.{' '}
              </span>
            )
          })
        }
      })
      .finally(() => {
        setBtnLoading(false)
      })
  }

  // 忘记密码相关
  const [timeDown, setTimeDown] = useState(0)
  const [resendLoading, setResendLoading] = useState(false)
  const [resetModal, showResetModal] = useState(false)
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (timeDown > 0) {
      const timeInterval = (timeCount: number) => {
        clearInterval(timer)
        setTimeDown(timeCount)
        timer = setInterval(() => {
          if (timeDown > 0) {
            setTimeDown((prevTime) => prevTime - 1)
          } else {
            clearInterval(timer)
            setErrorTips('')
          }
        }, 1000)
      }
      // 调用 timeInterval 函数
      timeInterval(timeDown)
    }
    // 清除定时器
    return () => {
      clearInterval(timer)
    }
  }, [timeDown])

  const handleForgotPwd = () => {
    if (!emailConfig.value) {
      handleBlur('email')
      return
    }
    setResendLoading(true)
    handleSendEmail()
    showResetModal(true)
  }

  // 发送邮件
  const handleSendEmail = () => {
    const params = {
      email: emailConfig.value
    }
    setErrorTips('')
    sendCustomerVerifEmail(params)
      .then((res) => {
        if (res.code === 200) {
          // 开始倒计时
          setTimeDown(60)
        }
      })
      .catch((err) => {
        setErrorTips(err.message)
      })
      .finally(() => {
        setResendLoading(false)
      })
  }

  // 三方登录相关
  const [bindModal, showBindModal] = useState(false)
  const [bindLoading, setBindLoading] = useState(false)
  const [fbLoading, setFbLoading] = useState(false)
  const [ggLoading, setGgLoading] = useState(false)
  useEffect(() => {
    if (!bindModal) {
      const fn = (prevConfig: any) => ({
        ...prevConfig,
        value: '',
        error: '',
        hasError: false
      })
      setBindEmailConfig(fn)
    }
  }, [bindModal])

  // gg-fb账户未绑定邮箱需要进行绑定
  const handleBindEmail = () => {
    const isPassValidate = validateForm({ isBind: true })
    if (!isPassValidate) {
      return
    }
    const params = {
      email: bindEmailConfig.value
    }
    setBindLoading(true)
    linkEmail(params)
      .then((res) => {
        if (res.code === 200) {
          loginCallback(res.data)
        } else {
          setBindEmailConfig({
            ...bindEmailConfig,
            error: res.message,
            hasError: true
          })
        }
      })
      .catch((err) => {
        setBindEmailConfig({
          ...bindEmailConfig,
          error: err.message,
          hasError: true
        })
      })
      .finally(() => {
        setBindLoading(false)
      })
  }

  // 三方信息传递给后端
  const handleThirdLogin = (type: string, token: string) => {
    if (!token) {
      message.error('token error')
      return
    }
    const params = {
      loginType: type,
      accessToken: token
    }
    if (type === 'facebook') {
      setFbLoading(true)
    } else {
      setGgLoading(true)
    }
    userThirdLogin(params)
      .then((res) => {
        // 登录成功
        if (res.code === 200) {
          loginCallback(res.data)
        } else if (res.code === 502) {
          // 绑定邮箱
          isMobile ? toPanelType(3) : showBindModal(true)
        } else {
          message.error(res.message)
        }
      })
      .finally(() => {
        setFbLoading(false)
        setGgLoading(false)
      })
  }

  // 唤起fb弹窗
  const fbLoginDialog = () => {
    try {
      window.FB &&
        window.FB.login(
          (response: any) => {
            console.log('fb-login', response)
            // facebook登录成功后回调函数
            if (response.status === 'connected') {
              const token = response.authResponse.accessToken
              console.log('Facebook登录成功:', response)
              handleThirdLogin('facebook', token)
            } else {
              console.error(`${response.status}: User not logged in.`)
            }
          },
          { scope: 'email' }
        )
    } catch (err) {
      console.error('init facebook error')
    }
  }

  // 渲染“使用 Google 帐号登录”按钮
  const renderGGButton = () => {
    window.google?.accounts?.id?.renderButton(
      document.getElementById('google-login-button'),
      {
        theme: 'outline',
        size: 'large',
        text: 'login_with',
        shape: 'rectangular',
        locale: 'en_US'
      }
    )
  }
  // 唤起gg弹窗--必须要手动点击才会触发, 所以要确保已经渲染按钮了
  const ggLoginDialog = () => {
    try {
      window.google?.accounts?.id?.initialize({
        client_id: ggAppId,
        cancel_on_tap_outside: true, // 控制是否在提示之外进行点击时取消提示(关闭一键登录弹窗)，默认true
        auto_select: false, // 开启自动登录功能，默认false
        callback: (response) => {
          console.log('Google登录成功', response)
          handleThirdLogin('google', response.credential)
        }, // 验证成功回调
        cancel: (error) => {
          message.error('Google Sign In failed: ' + error)
        }
      })

      renderGGButton()
      setGgLoading(false)
      // 启用一键登录提示(弹窗)功能
      // window.google.accounts.id.prompt()
    } catch (err) {
      console.error('init google error')
    }
  }

  // 点击gg按钮
  const ggLogin = () => {
    if (!window.google) {
      // 防止点击后，sdk未加载到
      setGgLoading(true)
      ggSdkCallback(ggLoginDialog)
    } else {
      ggLoginDialog()
    }
  }

  // 点击fb按钮
  const fbLogin = (isLoad: boolean) => {
    if (!window.FB) {
      // 防止点击后，sdk未加载到
      setFbLoading(true)
      fbSdkCallback(fbAppId, () => setFbLoading(false))
    } else {
      !isLoad && fbLoginDialog()
    }
  }

  // 初始化-sdk加载
  useEffect(() => {
    if (isMobile) {
      toPanelType(1) // 重置
    }
    !isProd && fbSdkCallback(fbAppId, isMobile ? () => fbLogin(true) : null)
    ggSdkCallback(isMobile ? ggLogin : null)
  }, [])

  // pc弹窗执行
  useEffect(() => {
    if (loginState.loginVisible) {
      ggLogin() // 可以提前渲染按钮id="google-login-button"
      !isProd && fbLogin(true)
    }
  }, [loginState.loginVisible])

  // 主要是处理面板切换后，gg按钮要重新初始化--有好办法再继续优化
  useEffect(() => {
    if ((loginState.loginVisible || isMobile) && [1, 2].includes(panelType)) {
      renderGGButton()
    }
  }, [panelType])

  return {
    checked,
    useChecked,
    subText,
    couponList,
    couponVisible,
    loginState,
    panelType,
    setPanelType,
    emailConfig,
    setEmailConfig,
    bindEmailConfig,
    passwordConfig,
    fullnameConfig,
    registerState,
    setRegisterState,
    btnLoading,
    setBtnLoading,
    resendLoading,
    setResendLoading,
    errorTips,
    setErrorTips,
    handleCancel,
    resetFormData,
    validateRegisterPassword,
    hanldeUpdateValue,
    handleBlur,
    loginCallback,
    handleRegister,
    handleSignIn,
    timeDown,
    setTimeDown,
    handleForgotPwd,
    resetModal,
    showResetModal,
    handleSendEmail,
    setCouponVisible,
    toPanelType,
    showRegPwdError,
    fbLogin,
    ggLogin,
    bindModal,
    bindLoading,
    showBindModal,
    handleBindEmail,
    fbLoading,
    ggLoading,
    urlParams, // m端用
    isProd
  }
}
